"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import { API_PORTAL_URL } from "@/lib/config";

// --- Type Declarations ---

export interface InitiatePaymentRequest {
  tempBookingId: string;
  amount: number;
  productInfo: string;
  firstname: string;
  email: string;
  phone: string;
  udf2?: string;
  udf3?: string;
  udf4?: string;
  udf5?: string;
  udf6?: string;
  udf7?: string;
  udf8?: string;
  udf9?: string;
  udf10?: string;
}

export interface VerifyPaymentResponse {
  success: boolean;
  bookingId?: string;
  amount?: number;
  status: string;
  error?: string;
  txnid?: string;
}

export type PaymentState =
  | "idle"
  | "initiating"
  | "open"
  | "verifying"
  | "success"
  | "failed"
  | "cancelled"
  | "error";

export interface UseEasebuzzPaymentReturn {
  state: PaymentState;
  error: string | null;
  bookingId: string | null;
  txnid: string | null;
  initiatePayment: (
    request: InitiatePaymentRequest,
  ) => Promise<VerifyPaymentResponse>;
  reset: () => void;
  canRetry: boolean;
}

declare global {
  interface Window {
    EasebuzzCheckout?: new (
      merchantKey: string,
      env: "test" | "prod",
    ) => {
      initiatePayment: (options: {
        access_key: string;
        onResponse: (response: any) => void;
      }) => void;
    };
  }
}

// --- Hook Implementation ---

const SDK_URL =
  "https://ebz-static.s3.ap-south-1.amazonaws.com/easecheckout/v2.0.0/easebuzz-checkout-v2.min.js";

// Global cache ensuring script handles are loaded exactly once per application cycle
let sdkInjectionPromise: Promise<void> | null = null;

/**
 * Safe script loader utility injecting Easebuzz Checkout JS libraries into context document.
 */
function loadEasebuzzSDK(): Promise<void> {
  if (typeof window === "undefined") {
    return Promise.reject(
      new Error(
        "Browser context environment is required to load payments SDK.",
      ),
    );
  }
  if (window.EasebuzzCheckout) {
    return Promise.resolve();
  }

  if (!sdkInjectionPromise) {
    sdkInjectionPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = SDK_URL;
      script.async = true;
      script.onload = () => {
        if (window.EasebuzzCheckout) {
          resolve();
        } else {
          reject(
            new Error(
              "Checkout script executed but registration on window context was not completed.",
            ),
          );
        }
      };
      script.onerror = () => {
        sdkInjectionPromise = null;
        reject(
          new Error(
            "Network error preventing acquisition of Easebuzz checkout library.",
          ),
        );
      };
      document.body.appendChild(script);
    });
  }

  return sdkInjectionPromise;
}

/**
 * Standard Hook orchestrating secure payments with Easebuzz checkout interfaces.
 */
export function useEasebuzzPayment(options?: {
  onSuccess?: (bookingId: string, txnid: string) => void;
  onFailure?: (error: string) => void;
}): UseEasebuzzPaymentReturn {
  const [state, setState] = useState<PaymentState>("idle");
  const [error, setError] = useState<string | null>(null);
  const [bookingId, setBookingId] = useState<string | null>(null);
  const [txnid, setTxnid] = useState<string | null>(null);

  // Core processing safety gates preventing race condition clicks
  const isProcessing = useRef<boolean>(false);
  const isUnmounted = useRef<boolean>(false);

  useEffect(() => {
    isUnmounted.current = false;
    return () => {
      isUnmounted.current = true;
    };
  }, []);

  /**
   * Resets status parameters back to basic setup ready for transaction retries.
   */
  const reset = useCallback(() => {
    if (isProcessing.current) {
      console.warn(
        "Cannot clear payment session while transaction processing is currently active.",
      );
      return;
    }
    setState("idle");
    setError(null);
    setBookingId(null);
    setTxnid(null);
  }, []);

  /**
   * Triggers checkout sequence (BFF authorization -> script verification -> iframe execution -> backend verification).
   */
  const initiatePayment = useCallback(
    async (request: InitiatePaymentRequest): Promise<VerifyPaymentResponse> => {
      if (isProcessing.current) {
        throw new Error(
          "An active transaction process is currently running on the client.",
        );
      }

      isProcessing.current = true;
      setError(null);
      setState("initiating");

      try {
        // Step 1: Query API BFF Route to obtain session key
        const initiateResponse = await fetch(
          `${API_PORTAL_URL}/Payments/Initiate`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(request),
          },
        );

        const initiateData = await initiateResponse.json();

        if (!initiateResponse.ok || !initiateData.success) {
          throw new Error(
            initiateData.error ||
              "Failed to establish transaction context on application BFF.",
          );
        }

        const {
          access_key,
          txnid: generatedTxnId,
          merchantKey,
          env,
        } = initiateData;

        if (isUnmounted.current) {
          throw new Error(
            "Hook context was destroyed prior to frame activation.",
          );
        }

        setTxnid(generatedTxnId);

        // Step 2: Inject script elements safely
        await loadEasebuzzSDK();

        if (!window.EasebuzzCheckout) {
          throw new Error(
            "Easebuzz checkout constructor objects were not resolved on global namespace.",
          );
        }

        if (isUnmounted.current) {
          throw new Error("Hook context destroyed during script loading.");
        }

        setState("open");

        // Step 3: Trigger checkout frame popup and wait for response
        const result = await new Promise<VerifyPaymentResponse>(
          (resolve, reject) => {
            const checkout = new window.EasebuzzCheckout!(merchantKey, env);

            checkout.initiatePayment({
              access_key,
              onResponse: async (response: any) => {
                if (isUnmounted.current) {
                  reject(
                    new Error(
                      "UI execution component destroyed during active payment callback.",
                    ),
                  );
                  return;
                }

                // Handle client-side exit cancel actions
                if (response.status === "userCancelled") {
                  setState("cancelled");
                  const cancelErrorMsg =
                    "Payment checkout process cancelled by user.";
                  setError(cancelErrorMsg);
                  if (options?.onFailure) options.onFailure(cancelErrorMsg);
                  resolve({
                    success: false,
                    status: "userCancelled",
                    txnid: generatedTxnId,
                  });
                  return;
                }

                // Step 4: Submit outcomes to verify backend route for final confirmation checks
                setState("verifying");

                try {
                  const verifyResponse = await fetch(
                    `${API_PORTAL_URL}/Payments/Verify`,
                    {
                      method: "POST",
                      headers: {
                        "Content-Type": "application/json",
                      },
                      body: JSON.stringify({
                        txnid: generatedTxnId,
                        frontendResponse: response,
                      }),
                    },
                  );

                  const verifyData = await verifyResponse.json();

                  if (isUnmounted.current) {
                    reject(
                      new Error(
                        "UI component destroyed prior to server transaction verification.",
                      ),
                    );
                    return;
                  }

                  if (!verifyResponse.ok || !verifyData.success) {
                    setState("failed");
                    const errorMsg =
                      verifyData.error ||
                      "Cryptographic transaction verification checks rejected.";
                    setError(errorMsg);
                    if (options?.onFailure) options.onFailure(errorMsg);
                    resolve({
                      success: false,
                      status: verifyData.status || "failed",
                      txnid: generatedTxnId,
                    });
                    return;
                  }

                  // Purchases verified and logged in system
                  setState("success");
                  setBookingId(verifyData.bookingId);

                  if (options?.onSuccess) {
                    options.onSuccess(verifyData.bookingId, generatedTxnId);
                  }

                  resolve({
                    success: true,
                    status: "success",
                    txnid: generatedTxnId,
                    bookingId: verifyData.bookingId,
                    amount: verifyData.amount,
                  });
                } catch (verifyError: any) {
                  if (isUnmounted.current) return;
                  setState("error");
                  const verificationFailureMsg = `Verification routing failure: ${verifyError.message}`;
                  setError(verificationFailureMsg);
                  if (options?.onFailure)
                    options.onFailure(verificationFailureMsg);
                  resolve({
                    success: false,
                    status: "error",
                    txnid: generatedTxnId,
                  });
                }
              },
            });
          },
        );

        isProcessing.current = false;
        return result;
      } catch (err: any) {
        isProcessing.current = false;
        if (!isUnmounted.current) {
          setState("error");
          setError(err.message);
          if (options?.onFailure) options.onFailure(err.message);
        }
        return { success: false, status: "error", txnid: "" };
      }
    },
    [options],
  );

  const canRetry =
    state === "failed" || state === "cancelled" || state === "error";

  return {
    state,
    error,
    bookingId,
    txnid,
    initiatePayment,
    reset,
    canRetry,
  };
}
